import { HttpError, useModalReturnType } from '@refinedev/core';
import { Modal, Table } from 'antd';
import { formatDate } from '../../../services/date';
import {
  translateAcademicRecordEventStatus,
  translateAcademicRecordStatus
} from 'services/academic-records';
import { useTable } from '@refinedev/antd';

type LogEvent = 'create' | 'update' | 'delete' | string;
type ChangeValue = string | number | null;
type ChangeTuple = [ChangeValue, ChangeValue];
type ChangeFieldKey = 'status' | 'score' | 'attendance_rate';

interface AcademicRecordLogAuthor {
  name?: string;
}

interface AcademicRecordLog {
  created_at: string;
  author?: AcademicRecordLogAuthor | string | null;
  event: LogEvent;
  status?: ChangeTuple | null;
  score?: ChangeTuple | null;
  attendance_rate?: ChangeTuple | null;
  justification?: ChangeTuple | null;
}

interface AcademicRecordLogModalProps {
  academicRecordId: string;
  modal: useModalReturnType;
}

interface ChangeField {
  key: ChangeFieldKey;
  label: string;
  empty: string;
  format: (value: ChangeValue) => string | null | undefined;
}

const STATUS_TAG_CLASS: Record<string, string> = {
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  failed: 'border-rose-200 bg-rose-50 text-rose-700',
  attendance_failed: 'border-rose-200 bg-rose-50 text-rose-700'
};

const CHANGE_FIELDS: ChangeField[] = [
  {
    key: 'status',
    label: 'Status',
    empty: 'Não definido',
    format: value =>
      typeof value === 'string'
        ? translateAcademicRecordStatus(value) ?? value
        : null
  },
  {
    key: 'score',
    label: 'Nota',
    empty: 'Nulo',
    format: value => (value == null ? null : String(value))
  },
  {
    key: 'attendance_rate',
    label: 'Frequência',
    empty: 'Nulo',
    format: value => (value == null ? null : `${value}%`)
  }
];

const getJustificationValue = (justification?: ChangeTuple | null) => {
  if (!Array.isArray(justification)) {
    return null;
  }

  const newValue = justification[1];

  if (newValue == null || newValue === '') {
    return null;
  }

  return String(newValue);
};

const getAuthorName = (author: AcademicRecordLog['author']) => {
  if (author && typeof author === 'object' && author.name) {
    return author.name;
  }

  return 'Sistema';
};

const formatChangeDisplay = (
  value: ChangeValue,
  empty: string,
  format: ChangeField['format']
) => {
  if (value == null || value === '') {
    return empty;
  }

  return format(value) ?? empty;
};

const ChangeValue = ({
  fieldKey,
  rawValue,
  displayValue
}: {
  fieldKey: ChangeFieldKey;
  rawValue: ChangeValue;
  displayValue: string;
}) => {
  if (fieldKey === 'status' && typeof rawValue === 'string') {
    const tagClass = STATUS_TAG_CLASS[rawValue];

    if (tagClass) {
      return (
        <span
          className={`inline-flex rounded-md border px-2.5 py-0.5 text-sm font-medium ${tagClass}`}
        >
          {displayValue}
        </span>
      );
    }
  }

  return <span>{displayValue}</span>;
};

const ChangesCell = ({ record }: { record: AcademicRecordLog }) => {
  const changes = CHANGE_FIELDS.flatMap(field => {
    const tuple = record[field.key];

    if (!Array.isArray(tuple)) {
      return [];
    }

    return [
      {
        key: field.key,
        label: field.label,
        from: tuple[0],
        to: tuple[1],
        fromDisplay: formatChangeDisplay(tuple[0], field.empty, field.format),
        toDisplay: formatChangeDisplay(tuple[1], field.empty, field.format)
      }
    ];
  });

  if (changes.length === 0) {
    return <span className="text-neutral-400">-</span>;
  }

  return (
    <div className="flex flex-col gap-2 py-1">
      {changes.map(change => (
        <div key={change.label} className="flex items-center gap-2">
          <span className="w-[104px] shrink-0 font-semibold text-neutral-800">
            {change.label}
          </span>
          <ChangeValue
            fieldKey={change.key}
            rawValue={change.from}
            displayValue={change.fromDisplay}
          />
          <span className="text-neutral-400">→</span>
          <ChangeValue
            fieldKey={change.key}
            rawValue={change.to}
            displayValue={change.toDisplay}
          />
        </div>
      ))}
    </div>
  );
};

export const AcademicRecordLogModal = ({
  academicRecordId,
  modal
}: AcademicRecordLogModalProps) => {
  const { tableProps } = useTable<AcademicRecordLog, HttpError>({
    resource: `graduate/academic_records/${academicRecordId}/logs`
  });

  return (
    <Modal
      title="Histórico de alterações"
      width={1100}
      visible={modal.visible}
      onCancel={modal.close}
      footer={null}
    >
      <Table
        {...tableProps}
        rowKey={record => `${record.created_at}-${record.event}`}
        columns={[
          {
            title: 'Data e hora',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: createdAt => (
              <span className="whitespace-nowrap text-neutral-700">
                {formatDate(createdAt, 'DD/MM/YYYY HH:mm')}
              </span>
            )
          },
          {
            title: 'Responsável',
            dataIndex: 'author',
            key: 'author',
            width: 140,
            render: author => (
              <span className="text-neutral-700">{getAuthorName(author)}</span>
            )
          },
          {
            title: 'Evento',
            dataIndex: 'event',
            key: 'event',
            width: 130,
            render: (event: string) => (
              <span>{translateAcademicRecordEventStatus(event)}</span>
            )
          },
          {
            title: 'Alterações',
            key: 'changes',
            render: (_: unknown, record: AcademicRecordLog) => (
              <ChangesCell record={record} />
            )
          },
          {
            title: 'Justificativa',
            dataIndex: 'justification',
            key: 'justification',
            width: 220,
            render: (justification: ChangeTuple | null) => {
              const value = getJustificationValue(justification);

              if (value == null) {
                return <span className="text-neutral-400">-</span>;
              }

              return <span className="text-neutral-700">{value}</span>;
            }
          }
        ]}
      />
    </Modal>
  );
};
